import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToolbar,
    useIonRouter
} from "@ionic/react";
import {
    alertCircleOutline,
    arrowBackOutline,
    calendarOutline,
    chevronDownOutline,
    chevronUpOutline,
    refreshOutline
} from "ionicons/icons";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    useLocation,
    useParams
} from "react-router-dom";
import {
    calculateFantasyGame,
    FantasyGameCalculation,
    FantasyRosterPlayer,
    FantasySettings,
    RealMatch
} from "../services/fantasyGameCalculator";
import FantasyHeaderActions from "../components/FantasyHeaderActions";
import {
    formatLocalMatchSchedule
} from "../services/fantasySchedule";

import Cookies from "js-cookie";
import "./FantasyGame.css";
import FantasyMatchModal from "../components/FantasyMatchModal";


interface RouteParams {
    id: string;
}

interface FantasyTeam {
    id: string;
    name: string;
    manager: string;
    roster: FantasyRosterPlayer[];
}
interface TeamProjectionSnapshot {
    totalProjectedPoints: number;
    starterPlayerIds: string[];
    players: Record<string, number>;
}

interface ProjectionSnapshot {
    capturedAt?: string;
    updatedAt?: string;

    myTeam: TeamProjectionSnapshot | null;
    opponentTeam: TeamProjectionSnapshot | null;
}

interface LiveProjectionPlayer {
    playerId: string;

    projection: number;

    pregameProjection: number;

    actualPoints: number;

    expectedPointsToDate: number;

    performanceDelta: number;

    remainingProjection: number;

    gameProgress: number;

    sensitivity: number;

    gameQuarter: number | null;

    gameStatus: string;

    isStarter: boolean;

    realTeamId: string;
}

interface TeamProjectionForecast {
    totalProjectedPoints: number;

    starterProjectedPoints?: number;

    benchProjectedPoints?: number;

    starterPlayerIds: string[];

    players: Record<
        string,
        LiveProjectionPlayer | number
    >;
}

interface ProjectionForecast {
    updatedAt?: string;

    realRound?: number;

    fantasyRound?: number;

    method?: string;

    myTeam: TeamProjectionForecast | null;

    opponentTeam: TeamProjectionForecast | null;
}

interface FantasyPlayerBreakdownItem {
    key: string;
    label: string;
    value?: string;
    points: number;
}

interface FantasyLivePlayerScore {
    id: string;
    points: number;
    breakdown?: FantasyPlayerBreakdownItem[];
}

interface FantasyLiveTeamScore {
    points: number;
    starterPoints: number;
    benchPoints: number;
    players: FantasyLivePlayerScore[];
}

interface FantasyLiveRound {
    realRound: number;
    fantasyRound: number;
    updatedAt: string;
    teams: Record<string, FantasyLiveTeamScore>;
}

interface FantasyGameState {
    success: boolean;
    updatedAt: string;
    scheduleTimezone?: string;
    projectionSnapshot?: ProjectionSnapshot | null;
    projectionForecast?: ProjectionForecast | null;

    liveRound?: FantasyLiveRound | null;

    league: {
        id: string;
        name: string;
        competitionId: string;
        competitionName: string;
    };

    week: {
        realRound: number;
        fantasyRound: number;
        status: string;
    };

    fantasyMatch: {
        id: string;
        isBye: boolean;
        myTeam: FantasyTeam;
        opponentTeam: FantasyTeam;
    };

    settings: FantasySettings;
    realMatches: RealMatch[];
}

function getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
        scheduled: "Programado",
        live: "En vivo",
        final: "Final",
        postponed: "Pospuesto",
        today: "Hoy"
    };

    return labels[status] || "Pendiente";
}

function getStatusClass(status: string): string {
    const classes: Record<string, string> = {
        scheduled: "fantasy-status--scheduled",
        live: "fantasy-status--live",
        final: "fantasy-status--final",
        postponed: "fantasy-status--postponed",
        today: "fantasy-status--today"
    };

    return classes[status] || "fantasy-status--scheduled";
}

function getStarterSlotOrder(slot: string): number {
    const normalizedSlot = slot
        .trim()
        .toUpperCase();

    const slotOrder: Record<string, number> = {
        QB: 1,
        RB: 2,
        WR: 3,
        FLEX: 4,
        K: 5,
        DEF: 6,

        /* Variantes compatibles para defensiva */
        DST: 6,
        "D/ST": 6
    };

    return slotOrder[normalizedSlot] ?? 99;
}

function sortStarters(
    starters: FantasyGameCalculation["myTeam"]["starters"]
): FantasyGameCalculation["myTeam"]["starters"] {
    return [...starters].sort(function (left, right) {
        const orderDifference =
            getStarterSlotOrder(left.slot) -
            getStarterSlotOrder(right.slot);

        if (orderDifference !== 0) {
            return orderDifference;
        }

        return left.name.localeCompare(
            right.name,
            "es-MX"
        );
    });
}

function formatPoints(points: number): string {
    return points.toFixed(2);
}
interface DisplayPlayer {
    id: string;
    name: string;
    teamName: string;
    position: string;
    slot: string;
    points: number;
    statLine: string;
    initialProjection: number | null;
    breakdown: FantasyPlayerBreakdownItem[];
}

function getOfficialStarterPoints(
    liveRound: FantasyLiveRound | null | undefined,
    fantasyTeamId: string
): number | null {
    const teamScore = liveRound?.teams?.[fantasyTeamId];

    if (!teamScore) {
        return null;
    }

    const points = Number(teamScore.starterPoints);

    return Number.isFinite(points)
        ? points
        : null;
}

function getOfficialPlayerPoints(
    liveRound: FantasyLiveRound | null | undefined,
    fantasyTeamId: string
): Record<string, number> {
    const teamScore = liveRound?.teams?.[fantasyTeamId];

    if (!teamScore || !Array.isArray(teamScore.players)) {
        return {};
    }

    const pointsByPlayerId: Record<string, number> = {};

    teamScore.players.forEach(function (player) {
        const playerId = String(player.id || "").trim();
        const points = Number(player.points);

        if (
            playerId !== "" &&
            Number.isFinite(points)
        ) {
            pointsByPlayerId[playerId] = points;
        }
    });

    return pointsByPlayerId;
}

function getOfficialPlayerBreakdowns(
    liveRound: FantasyLiveRound | null | undefined,
    fantasyTeamId: string
): Record<string, FantasyPlayerBreakdownItem[]> {
    const teamScore = liveRound?.teams?.[fantasyTeamId];

    if (!teamScore || !Array.isArray(teamScore.players)) {
        return {};
    }

    const breakdownsByPlayerId: Record<
        string,
        FantasyPlayerBreakdownItem[]
    > = {};

    teamScore.players.forEach(function (player) {
        const playerId = String(player.id || "").trim();

        if (playerId === "") {
            return;
        }

        const breakdown = Array.isArray(player.breakdown)
            ? player.breakdown.filter(function (item) {
                return (
                    String(item.label || "").trim() !== "" &&
                    Number.isFinite(Number(item.points))
                );
            })
            : [];

        breakdownsByPlayerId[playerId] = breakdown;
    });

    return breakdownsByPlayerId;
}

function getLiveForecastPlayerProjections(
    teamForecast:
        | TeamProjectionForecast
        | null
        | undefined
): Record<string, number> {
    if (
        !teamForecast ||
        !teamForecast.players
    ) {
        return {};
    }

    const projections: Record<string, number> = {};

    Object.entries(
        teamForecast.players
    ).forEach(function ([
        playerId,
        forecast
    ]) {
        const projection =
            typeof forecast === "number"
                ? forecast
                : Number(
                    forecast?.projection
                );

        if (
            playerId.trim() !== "" &&
            Number.isFinite(projection)
        ) {
            projections[playerId] = projection;
        }
    });

    return projections;
}

function getFallbackForecastPlayerProjections(
    teamForecast:
        | TeamProjectionForecast
        | TeamProjectionSnapshot
        | null
        | undefined
): Record<string, number> {
    if (
        !teamForecast ||
        !teamForecast.players
    ) {
        return {};
    }

    const projections: Record<string, number> = {};

    Object.entries(
        teamForecast.players
    ).forEach(function ([
        playerId,
        value
    ]) {
        /*
            Forecast live:
            {
              projection: 14.2,
              pregameProjection: 12.4
            }

            Forecast histórico:
            12.4
        */
        if (
            typeof value === "object" &&
            value !== null
        ) {
            const forecast = value as LiveProjectionPlayer;

            const projection = Number(
                forecast.pregameProjection ??
                forecast.projection
            );

            if (Number.isFinite(projection)) {
                projections[playerId] = projection;
            }

            return;
        }

        const projection = Number(value);

        if (Number.isFinite(projection)) {
            projections[playerId] = projection;
        }
    });

    return projections;
}

function getLiveForecastPlayerDetails(
    teamForecast: TeamProjectionForecast | null | undefined
): Record<string, LiveProjectionPlayer> {
    if (!teamForecast?.players) {
        return {};
    }

    const details: Record<string, LiveProjectionPlayer> = {};

    Object.entries(teamForecast.players).forEach(function ([
        playerId,
        value
    ]) {
        if (
            typeof value === "object" &&
            value !== null
        ) {
            details[playerId] = value;
        }
    });

    return details;
}

function PlayerRow(props: {
    player: DisplayPlayer;
    isExpanded: boolean;
    onToggle: () => void;
}): JSX.Element {
    const {
        player,
        isExpanded,
        onToggle
    } = props;

    const breakdown = player.breakdown || [];
    const hasBreakdown = breakdown.length > 0;

    return (
        <article
            className={[
                "fantasy-player-row",
                isExpanded
                    ? "fantasy-player-row--expanded"
                    : ""
            ].join(" ")}
        >
            <button
                type="button"
                className="fantasy-player-row__summary"
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? "Ocultar" : "Ver"} desglose de ${player.name}`}
                onClick={onToggle}
            >

                <div className="fantasy-player-row__main">
                    <strong>{player.name}</strong>

                    <span>
                        {player.position} · {player.teamName}
                    </span>

                    <small>{player.statLine}</small>
                </div>

                <div className="fantasy-player-row__score">
                    <strong>
                        {formatPoints(player.points)}
                    </strong>

                    <small>REAL</small>

                    <em>
                        PROY.{" "}
                        {player.initialProjection === null
                            ? "—"
                            : formatPoints(
                                player.initialProjection
                            )}
                    </em>
                </div>
            </button>

            {isExpanded ? (
                <div className="fantasy-player-row__breakdown">
                    {hasBreakdown ? (
                        breakdown.map(function (item, index) {
                            const points = Number(item.points);

                            return (
                                <div
                                    className="fantasy-player-row__breakdown-line"
                                    key={`${item.key}-${index}`}
                                >
                                    <span>
                                        <strong>{item.label}</strong>

                                        {item.value ? (
                                            <small>{item.value}</small>
                                        ) : null}
                                    </span>

                                    <b
                                        className={
                                            points < 0
                                                ? "fantasy-player-row__breakdown-points fantasy-player-row__breakdown-points--negative"
                                                : "fantasy-player-row__breakdown-points"
                                        }
                                    >
                                        {points > 0 ? "+" : ""}
                                        {formatPoints(points)}
                                    </b>
                                </div>
                            );
                        })
                    ) : (
                        <p className="fantasy-player-row__breakdown-empty">
                            Aún no hay estadísticas registradas para este jugador.
                        </p>
                    )}
                </div>
            ) : null}
        </article>
    );
}

function RosterCard(props: {
    rosterKey: string;
    starters: FantasyGameCalculation["myTeam"]["starters"];
    bench: FantasyGameCalculation["myTeam"]["bench"];
    playerProjections: Record<string, number>;
    officialPlayerPoints: Record<string, number>;
    officialPlayerBreakdowns: Record<
        string,
        FantasyPlayerBreakdownItem[]
    >;
    expandedRowId: string | null;
    onToggleRow: (rowId: string) => void;
}): JSX.Element {

    const {
        rosterKey,
        starters,
        bench,
        playerProjections,
        officialPlayerPoints,
        officialPlayerBreakdowns,
        expandedRowId,
        onToggleRow
    } = props;

    const sortedStarters = sortStarters(starters);

function toDisplayPlayer(
    player: FantasyGameCalculation["myTeam"]["starters"][number]
): DisplayPlayer {
    const officialPoints =
        officialPlayerPoints[player.id];

    return {
        ...player,

        /*
            Si el backend devolvió el puntaje oficial,
            éste sustituye cualquier cálculo local.
        */
        points: Number.isFinite(officialPoints)
            ? officialPoints
            : player.points,

        initialProjection:
            playerProjections[player.id] ?? null,

        breakdown: officialPlayerBreakdowns[player.id] ?? []
    };
}

    return (
        <section className="fantasy-roster-card">
            <div className="fantasy-roster-card__section">
                <h3>Titulares</h3>

                {sortedStarters.length > 0 ? (
                    sortedStarters.map(function (player) {
                        const rowId = `${rosterKey}:${player.id}`;

                        return (
                            <PlayerRow
                                key={player.id}
                                player={toDisplayPlayer(player)}
                                isExpanded={expandedRowId === rowId}
                                onToggle={function () {
                                    onToggleRow(rowId);
                                }}
                            />
                        );
                    })
                ) : (
                    <p className="fantasy-empty">
                        No hay titulares registrados.
                    </p>
                )}
            </div>

            <div className="fantasy-roster-card__section fantasy-roster-card__section--bench">
                <h3>Banca</h3>

                {bench.length > 0 ? (
                    bench.map(function (player) {
                        const rowId = `${rosterKey}:${player.id}`;

                        return (
                            <PlayerRow
                                key={player.id}
                                player={toDisplayPlayer(player)}
                                isExpanded={expandedRowId === rowId}
                                onToggle={function () {
                                    onToggleRow(rowId);
                                }}
                            />
                        );
                    })
                ) : (
                    <p className="fantasy-empty">
                        No hay jugadores en banca.
                    </p>
                )}
            </div>
        </section>
    );
}

function getTeamMatchStatus(
    realMatches: RealMatch[],
    realTeamId: string
): string {
    const match = realMatches.find(function (candidate) {
        return (
            candidate.local.id === realTeamId ||
            candidate.visitor.id === realTeamId
        );
    });

    return String(match?.status || "scheduled")
        .trim()
        .toLowerCase();
}

type PlayerWithRealTeam = Pick<
    FantasyRosterPlayer,
    "teamId"
>;

function hasPlayerStartedGame(
    realMatches: RealMatch[],
    player: PlayerWithRealTeam
): boolean {
    const status = getTeamMatchStatus(
        realMatches,
        player.teamId
    );

    return (
        status === "live" ||
        status === "final"
    );
}

function hasPlayerFinishedGame(
    realMatches: RealMatch[],
    player: PlayerWithRealTeam
): boolean {
    return getTeamMatchStatus(
        realMatches,
        player.teamId
    ) === "final";
}

function getInitialStarterProjection(
    starters: FantasyGameCalculation["myTeam"]["starters"],
    playerProjections: Record<string, number>,
    fallbackTotal: number | null
): number | null {
    if (starters.length === 0) {
        return fallbackTotal;
    }

    return starters.reduce(function (
        total,
        player
    ): number {
        const projection = Number(
            playerProjections[player.id] ?? 0
        );

        return total + (
            Number.isFinite(projection)
                ? projection
                : 0
        );
    }, 0);
}

function getCurrentTeamProjection(
    starters: FantasyGameCalculation["myTeam"]["starters"],
    playerProjections: Record<string, number>,
    officialPlayerPoints: Record<string, number>,
    realMatches: RealMatch[],
    initialTotal: number | null
): number | null {
    if (initialTotal === null) {
        return null;
    }

    if (starters.length === 0) {
        return initialTotal;
    }

    /*
        Cuando todos los titulares terminaron, se vuelve a mostrar
        la proyección inicial para comparar contra el total real.
    */
    const allStartersFinished = starters.every(
        function (player) {
            return hasPlayerFinishedGame(
                realMatches,
                player
            );
        }
    );

    if (allStartersFinished) {
        return initialTotal;
    }

    /*
        Mientras haya actividad:
        - Titular que ya inició: puntos reales actuales.
        - Titular que aún no inicia: proyección inicial.
    */
    return starters.reduce(function (
        total,
        player
    ): number {
        const initialProjection = Number(
            playerProjections[player.id] ?? 0
        );

        if (!hasPlayerStartedGame(realMatches, player)) {
            return total + (
                Number.isFinite(initialProjection)
                    ? initialProjection
                    : 0
            );
        }

        const realPoints = Number(
            officialPlayerPoints[player.id] ??
            player.points ??
            0
        );

        return total + (
            Number.isFinite(realPoints)
                ? realPoints
                : 0
        );
    }, 0);
}

function isTeamProjectionDynamic(
    starters: FantasyGameCalculation["myTeam"]["starters"],
    realMatches: RealMatch[]
): boolean {
    if (starters.length === 0) {
        return false;
    }

    const hasStartedStarter = starters.some(
        function (player) {
            return hasPlayerStartedGame(
                realMatches,
                player
            );
        }
    );

    const allStartersFinished = starters.every(
        function (player) {
            return hasPlayerFinishedGame(
                realMatches,
                player
            );
        }
    );

    return hasStartedStarter && !allStartersFinished;
}

const FantasyGame: React.FC = function () {
    const { id } = useParams<RouteParams>();

    const router = useIonRouter();
    const location = useLocation();
    
    const [
        expandedRowId,
        setExpandedRowId
    ] = useState<string | null>(null);

    const [data, setData] =
        useState<FantasyGameState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [
    selectedRealMatch,
    setSelectedRealMatch
] = useState<RealMatch | null>(null);
    const [isRealScheduleOpen, setIsRealScheduleOpen] =
    useState(false);

    function toggleExpandedRow(
    rowId: string
): void {
    setExpandedRowId(function (currentRowId) {
        return currentRowId === rowId
            ? null
            : rowId;
    });
}

function goBackFromFantasyGame(): void {
    router.push(
        `/fantasyTeam/${id}`,
        "back",
        "replace"
    );
}

const loadFantasyGame = useCallback(
    async function (
        showLoader = false
    ): Promise<void> {
        if (!id) {
            setError("No se especificó la liga fantasy.");
            setLoading(false);
            return;
        }

        if (showLoader) {
            setRefreshing(true);
        }

        try {
            const accessToken =
                Cookies.get("access_token") || "";

            const accessUsername =
                Cookies.get("access_username") || "";

                console.log("Autenticación enviada:", {
                    username: accessUsername,
                    hasToken: Boolean(accessToken)
                });

            const response = await fetch(
                "https://fantasy.ftblscore.com/create_league/" +
                    `control_FantasyGameState.php?id=${encodeURIComponent(id)}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        "X-Access-Token": accessToken,
                        "X-Access-Username": accessUsername
                    }
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "No fue posible cargar el enfrentamiento."
                );
            }

            setData(result);
            setError("");
        } catch (requestError) {
            setError(
                requestError instanceof Error
                    ? requestError.message
                    : "Ocurrió un error al cargar la información."
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    },
    [id]
);


    useEffect(
        function () {
            loadFantasyGame();
        },
        [loadFantasyGame]
    );

    /*
        La frecuencia cambia según el estado de la jornada:
        live      → 30 segundos
        today     → 60 segundos
        scheduled → 5 minutos
        final     → no consulta automáticamente
    */
    useEffect(
        function () {
            if (!data) {
                return;
            }

            const pollingTimes: Record<string, number> = {
                live: 30000,
                today: 60000,
                scheduled: 300000
            };

            const refreshTime =
                pollingTimes[data.week.status];

            if (!refreshTime) {
                return;
            }

            const intervalId = window.setInterval(
                function () {
                    loadFantasyGame();
                },
                refreshTime
            );

            return function () {
                window.clearInterval(intervalId);
            };
        },
        [
            data,
            loadFantasyGame
        ]
    );

    const calculation = useMemo(
        function (): FantasyGameCalculation | null {
            if (!data) {
                return null;
            }

            return calculateFantasyGame(
                data.settings,
                data.fantasyMatch.myTeam.roster || [],
                data.fantasyMatch.opponentTeam.roster || [],
                data.realMatches || []
            );
        },
        [data]
    );


    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
    fill="clear"
    aria-label="Regresar"
    onClick={goBackFromFantasyGame}
>
    <IonIcon
        slot="icon-only"
        icon={arrowBackOutline}
    />
</IonButton>

                        </IonButtons>

                        <IonTitle>Fantasy</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <div className="fantasy-loading">
                        <IonSpinner name="crescent" />
                        <p>Cargando enfrentamiento...</p>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    if (error || !data || !calculation) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
    fill="clear"
    aria-label="Regresar"
    onClick={goBackFromFantasyGame}
>
    <IonIcon
        slot="icon-only"
        icon={arrowBackOutline}
    />
</IonButton>
                        </IonButtons>

                        <IonTitle>Fantasy</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <main className="fantasy-error">
                        <IonIcon icon={alertCircleOutline} />

                        <h1>No fue posible cargar la jornada</h1>

                        <p>
                            {error ||
                                "La información no está disponible."}
                        </p>

                        <IonButton
                            onClick={function () {
                                loadFantasyGame(true);
                            }}
                        >
                            Intentar nuevamente
                        </IonButton>
                    </main>
                </IonContent>
            </IonPage>
        );
    }

const myOfficialStarterPoints =
    getOfficialStarterPoints(
        data.liveRound,
        data.fantasyMatch.myTeam.id
    );

const opponentOfficialStarterPoints =
    getOfficialStarterPoints(
        data.liveRound,
        data.fantasyMatch.opponentTeam.id
    );

const myPoints = myOfficialStarterPoints !== null
    ? myOfficialStarterPoints
    : calculation.myTeam.totalPoints;

const opponentPoints =
    opponentOfficialStarterPoints !== null
        ? opponentOfficialStarterPoints
        : calculation.opponentTeam.totalPoints;

const myOfficialPlayerPoints =
    getOfficialPlayerPoints(
        data.liveRound,
        data.fantasyMatch.myTeam.id
    );

const opponentOfficialPlayerPoints =
    getOfficialPlayerPoints(
        data.liveRound,
        data.fantasyMatch.opponentTeam.id
    );

    const myOfficialPlayerBreakdowns =
    getOfficialPlayerBreakdowns(
        data.liveRound,
        data.fantasyMatch.myTeam.id
    );

const opponentOfficialPlayerBreakdowns =
    getOfficialPlayerBreakdowns(
        data.liveRound,
        data.fantasyMatch.opponentTeam.id
    );

/*
    La proyección inicial no cambia durante la jornada.

    Se prefiere ProjectionSnapshot porque representa la proyección
    capturada para la alineación de esa jornada. Forecast sólo es
    respaldo antes de que exista el snapshot.
*/
/*
    La proyección inicial no cambia durante la jornada.

    ProjectionSnapshot es la fuente preferida porque conserva
    la proyección capturada para la alineación de la jornada.
    ProjectionForecast sólo funciona como respaldo.
*/
/*
    Snapshot:
    proyección original de la jornada, fija para auditoría.

    Forecast:
    proyección dinámica calculada con puntos actuales,
    progreso del partido y rendimiento contra expectativa.
*/
const mySnapshotProjectionData =
    data.projectionSnapshot?.myTeam ?? null;

const opponentSnapshotProjectionData =
    data.projectionSnapshot?.opponentTeam ?? null;

const myForecastProjectionData =
    data.projectionForecast?.myTeam ?? null;

const opponentForecastProjectionData =
    data.projectionForecast?.opponentTeam ?? null;


/*
    Proyecciones individuales iniciales.
    Nunca se sustituyen por los puntos reales.
*/
/*
    Proyecciones originales de pregame.
*/
const myInitialPlayerProjections =
    mySnapshotProjectionData?.players ??
    getFallbackForecastPlayerProjections(
        myForecastProjectionData
    );

const opponentInitialPlayerProjections =
    opponentSnapshotProjectionData?.players ??
    getFallbackForecastPlayerProjections(
        opponentForecastProjectionData
    );

/*
    Forecasts actuales.
    Si por alguna razón no llega forecast dinámico,
    se conserva la proyección inicial como fallback.
*/
const myLivePlayerProjections = {
    ...myInitialPlayerProjections,

    ...getLiveForecastPlayerProjections(
        myForecastProjectionData
    )
};

const opponentLivePlayerProjections = {
    ...opponentInitialPlayerProjections,

    ...getLiveForecastPlayerProjections(
        opponentForecastProjectionData
    )
};

const myLiveForecastDetails =
    getLiveForecastPlayerDetails(
        myForecastProjectionData
    );

const opponentLiveForecastDetails =
    getLiveForecastPlayerDetails(
        opponentForecastProjectionData
    );

/*
    Total pregame guardado como respaldo.
*/
const myProjectionSnapshotTotal =
    mySnapshotProjectionData?.
        totalProjectedPoints ?? null;

const opponentProjectionSnapshotTotal =
    opponentSnapshotProjectionData?.
        totalProjectedPoints ?? null;

/*
    Proyección inicial:
    suma de proyecciones originales de titulares.
*/
const myInitialProjection = getInitialStarterProjection(
    calculation.myTeam.starters,
    myInitialPlayerProjections,
    myProjectionSnapshotTotal
);

const opponentInitialProjection =
    getInitialStarterProjection(
        calculation.opponentTeam.starters,
        opponentInitialPlayerProjections,
        opponentProjectionSnapshotTotal
    );

/*
    Proyección dinámica:
    - Jugador cuyo juego no inicia: conserva su proyección inicial.
    - Jugador en vivo o finalizado: usa sus puntos reales.
    - Si todos los titulares terminaron: vuelve a la inicial.
*/
const myAllStartersFinished =
    calculation.myTeam.starters.length > 0 &&
    calculation.myTeam.starters.every(function (player) {
        return hasPlayerFinishedGame(
            data.realMatches,
            player
        );
    });

const myCurrentProjection = myAllStartersFinished
    ? myInitialProjection
    : Number.isFinite(
        Number(
            myForecastProjectionData?.totalProjectedPoints
        )
    )
        ? Number(
            myForecastProjectionData?.totalProjectedPoints
        )
        : getCurrentTeamProjection(
            calculation.myTeam.starters,
            myInitialPlayerProjections,
            myOfficialPlayerPoints,
            data.realMatches,
            myInitialProjection
        );

const opponentCurrentProjection =
    Number.isFinite(
        Number(
            opponentForecastProjectionData?.
                totalProjectedPoints
        )
    )
        ? Number(
            opponentForecastProjectionData?.
                totalProjectedPoints
        )
        : getCurrentTeamProjection(
            calculation.opponentTeam.starters,
            opponentInitialPlayerProjections,
            opponentOfficialPlayerPoints,
            data.realMatches,
            opponentInitialProjection
        );

const hasMyLiveForecast =
    Boolean(
        myForecastProjectionData &&
        Object.keys(
            myForecastProjectionData.players || {}
        ).length > 0
    );

const hasOpponentLiveForecast =
    Boolean(
        opponentForecastProjectionData &&
        Object.keys(
            opponentForecastProjectionData.players || {}
        ).length > 0
    );

const myProjectionLabel = hasMyLiveForecast
    ? "PROY. ACTUAL"
    : "PROY. INICIAL";

const opponentProjectionLabel = hasOpponentLiveForecast
    ? "PROY. ACTUAL"
    : "PROY. INICIAL";

    const isBye = data.fantasyMatch.isBye === true;
    const liveMatchesCount = data.realMatches.filter(
    function (match) {
        return match.status === "live";
    }
).length;



    const isWinning = !isBye && myPoints > opponentPoints;
    const isLosing = !isBye && myPoints < opponentPoints;
    const isTied = !isBye && myPoints === opponentPoints;


    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton
    fill="clear"
    aria-label="Regresar"
    onClick={goBackFromFantasyGame}
>
    <IonIcon
        slot="icon-only"
        icon={arrowBackOutline}
    />
</IonButton>
                    </IonButtons>

                    <IonTitle>
                        {data.league.name}
                    </IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            fill="clear"
                            aria-label="Actualizar enfrentamiento"
                            disabled={refreshing}
                            onClick={function () {
                                loadFantasyGame(true);
                            }}
                        >
                            {refreshing ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <IonIcon icon={refreshOutline} />
                            )}
                        </IonButton>
                    </IonButtons>

                    <FantasyHeaderActions
                        leagueId={id || ""}
                        screen="game"
                    />

                </IonToolbar>
            </IonHeader>

            <IonContent
                fullscreen
                scrollEvents
                onIonScrollStart={function () {
                    setExpandedRowId(null);
                }}
            >
                <main className="fantasy-game">
                    <section className="fantasy-game__hero">
                        <span className="fantasy-game__competition">
                            {data.league.competitionName}
                        </span>

                        <h1>
                            Jornada {data.week.realRound}
                        </h1>

                        <div className="fantasy-game__status-row">
                            <IonBadge
                                className={getStatusClass(
                                    data.week.status
                                )}
                            >
                                {getStatusLabel(
                                    data.week.status
                                )}
                            </IonBadge>

                            <span>
                                Actualizado{" "}
                                {new Intl.DateTimeFormat(
                                    "es-MX",
                                    {
                                        hour: "2-digit",
                                        minute: "2-digit"
                                    }
                                ).format(
                                    new Date(data.updatedAt)
                                )}
                            </span>
                        </div>
                    </section>

                    <section className="fantasy-scoreboard">
                        <article className="fantasy-scoreboard__team">
                            <span>
                                {data.fantasyMatch.myTeam.name}
                            </span>
<div className="fantasy-scoreboard__score-group">
    <strong
        className={
            isWinning
                ? "fantasy-scoreboard__score fantasy-scoreboard__score--winning"
                : "fantasy-scoreboard__score"
        }
    >
        {formatPoints(myPoints)}
    </strong>

    <span className="fantasy-scoreboard__projection">
{myProjectionLabel}:{" "}
{myCurrentProjection === null
    ? "—"
    : formatPoints(myCurrentProjection)}
    </span>
</div>

<small className="fantasy-scoreboard__manager">
    {data.fantasyMatch.myTeam.manager}
</small>

                        </article>

                        <div className="fantasy-scoreboard__versus">
                            <span>
                                {isBye
                                    ? "Semana bye"
                                    : isTied
                                        ? "Empate"
                                        : isWinning
                                            ? "Vas ganando"
                                            : isLosing
                                                ? "Vas perdiendo"
                                                : "VS"}
                            </span>

                            <strong>
                                {isBye ? "—" : "VS"}
                            </strong>
                        </div>


                        <article className="fantasy-scoreboard__team fantasy-scoreboard__team--opponent">
                            <span>
                                {isBye
                                    ? "Sin rival"
                                    : data.fantasyMatch.opponentTeam.name}
                            </span>

<div className="fantasy-scoreboard__score-group">
    <strong
        className={
            isLosing
                ? "fantasy-scoreboard__score fantasy-scoreboard__score--winning"
                : "fantasy-scoreboard__score"
        }
    >
        {isBye
            ? "—"
            : formatPoints(opponentPoints)}
    </strong>

    <span className="fantasy-scoreboard__projection">
        {isBye
            ? "Sin rival"
            : `${opponentProjectionLabel}: ${
    opponentCurrentProjection === null
        ? "—"
        : formatPoints(opponentCurrentProjection)
}`}
    </span>
</div>

<small className="fantasy-scoreboard__manager">
    {isBye
        ? "No tienes rival esta jornada"
        : data.fantasyMatch.opponentTeam.manager}
</small>


                        </article>
                    </section>
<section className="fantasy-real-schedule">
    <button
        type="button"
        className={[
            "fantasy-real-schedule__toggle",
            isRealScheduleOpen
                ? "fantasy-real-schedule__toggle--open"
                : ""
        ].join(" ")}
        aria-expanded={isRealScheduleOpen}
        aria-controls="fantasy-real-schedule-list"
        onClick={function () {
            setIsRealScheduleOpen(function (currentValue) {
                return !currentValue;
            });
        }}
    >
        <span className="fantasy-real-schedule__icon">
            <IonIcon icon={calendarOutline} />
        </span>

        <span className="fantasy-real-schedule__copy">
            <small>PARTIDOS</small>

            <strong>
                {liveMatchesCount > 0
                    ? `${liveMatchesCount} partido${
                        liveMatchesCount === 1 ? "" : "s"
                    } en vivo`
                    : `Ver calendario · ${data.realMatches.length} partidos`}
            </strong>
        </span>

        <span className="fantasy-real-schedule__action">
            {isRealScheduleOpen ? "Cerrar" : "Abrir"}

            <IonIcon
                icon={
                    isRealScheduleOpen
                        ? chevronUpOutline
                        : chevronDownOutline
                }
            />
        </span>
    </button>

    {isRealScheduleOpen && (
        <div
            className="fantasy-real-schedule__content"
            id="fantasy-real-schedule-list"
        >
            <div className="fantasy-real-matches">
                {data.realMatches.map(function (match) {
                    return (
                        <article
                            className="fantasy-real-match fantasy-real-match--selectable"
                            key={match.id}
                            role="button"
                            tabIndex={0}
                            aria-label={`Abrir partido entre ${match.local.name} y ${match.visitor.name}`}
                            onClick={function () {
                                setSelectedRealMatch(match);
                            }}
                            onKeyDown={function (event) {
                                if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                ) {
                                    event.preventDefault();
                                    setSelectedRealMatch(match);
                                }
                            }}
                        >
                            <div className="fantasy-real-match__meta">
                                <span>
                                    {formatLocalMatchSchedule(
                                        match.date,
                                        match.hour,
                                        data.scheduleTimezone ||
                                            "America/Monterrey"
                                    ) || "Horario pendiente"}
                                </span>

                                <IonBadge
                                    className={getStatusClass(
                                        match.status
                                    )}
                                >
                                    {getStatusLabel(match.status)}
                                </IonBadge>
                            </div>


                            <div className="fantasy-real-match__score">
                                <strong>{match.local.name}</strong>

                                <span>
                                    {match.local.score || "—"}
                                </span>

                                <b>-</b>

                                <span>
                                    {match.visitor.score || "—"}
                                </span>

                                <strong>{match.visitor.name}</strong>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    )}
</section>
                    <section className="fantasy-section">
                        <div className="fantasy-section__title">
                            <div>
                                <span>Comparativa</span>
                                <h2>Alineaciones</h2>
                            </div>

                            <p>
                                Sólo titulares suman al marcador.
                            </p>
                        </div>

                        <div className="fantasy-rosters">
<RosterCard
    rosterKey="my-team"
    starters={calculation.myTeam.starters}
    bench={calculation.myTeam.bench}
    playerProjections={myInitialPlayerProjections}
    officialPlayerPoints={myOfficialPlayerPoints}
    officialPlayerBreakdowns={myOfficialPlayerBreakdowns}
    expandedRowId={expandedRowId}
    onToggleRow={toggleExpandedRow}
/>

<RosterCard
    rosterKey="opponent-team"
    starters={calculation.opponentTeam.starters}
    bench={calculation.opponentTeam.bench}
    playerProjections={
        isBye
            ? {}
            : opponentInitialPlayerProjections
    }
    officialPlayerPoints={
        isBye
            ? {}
            : opponentOfficialPlayerPoints
    }
    officialPlayerBreakdowns={
        isBye
            ? {}
            : opponentOfficialPlayerBreakdowns
    }
    expandedRowId={expandedRowId}
    onToggleRow={toggleExpandedRow}
/>
                        </div>
                    </section>

                </main>
            </IonContent>
            <FantasyMatchModal
    isOpen={selectedRealMatch !== null}
    match={selectedRealMatch}
    fantasySettings={data.settings}
    onDismiss={function () {
        setSelectedRealMatch(null);
    }}
/>

        </IonPage>
    );
};

export default FantasyGame;
